import { useState, useEffect } from "react";
import { 
    ArrowLeft,
    Check,
    X,
    Zap,
    Building2,
    Sparkles,
    Globe,
    Calculator,
    ShoppingBag,
    Users,
    FileText,
    Palette,
    Download,
    BarChart3,
    Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import SEO, { SEO_CONFIG } from "./components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

const PLANS = [
    {
        id: "starter",
        name: "Starter",
        description: "Perfect for getting started",
        monthlyPrice: 0,
        annualPrice: 0,
        icon: Globe,
        color: "gray",
        popular: false,
        features: [
            { text: "Basic website score", included: true },
            { text: "Limited feedback", included: true },
            { text: "3 scans per month", included: true },
            { text: "Full score breakdown", included: false },
            { text: "Profit Leak Calculator", included: false },
            { text: "Shopify Optimization Check", included: false },
            { text: "Referral Program Builder", included: false },
            { text: "PDF exports", included: false },
            { text: "White label reports", included: false }
        ],
        cta: "Get Started Free",
        ctaVariant: "outline"
    },
    {
        id: "pro",
        name: "Pro",
        description: "For growing businesses",
        monthlyPrice: 19,
        annualPrice: 114, // 50% off ($228 → $114)
        icon: Zap,
        color: "violet",
        popular: true,
        features: [
            { text: "Basic website score", included: true },
            { text: "Unlimited scans", included: true },
            { text: "Full score breakdown", included: true },
            { text: "Profit Leak Calculator", included: true },
            { text: "Shopify Optimization Check", included: true },
            { text: "Referral Program Builder", included: true },
            { text: "PDF exports", included: true },
            { text: "Score history & tracking", included: true },
            { text: "White label reports", included: false }
        ],
        cta: "Start Pro Trial",
        ctaVariant: "default"
    },
    {
        id: "agency",
        name: "Agency",
        description: "For agencies & teams",
        monthlyPrice: 69,
        annualPrice: 414, // 50% off ($828 → $414)
        icon: Building2,
        color: "emerald",
        popular: false,
        features: [
            { text: "Everything in Pro", included: true },
            { text: "White label reports", included: true },
            { text: "Remove Site Grade Pro branding", included: true },
            { text: "Client export tools", included: true },
            { text: "Bulk scan capabilities", included: true },
            { text: "Team collaboration", included: true },
            { text: "Priority support", included: true },
            { text: "Custom branding", included: true },
            { text: "API access", included: true }
        ],
        cta: "Contact Sales",
        ctaVariant: "outline"
    }
];

export default function PricingPage({ onBack, onSelectPlan, onNavigate }) {
    const [isAnnual, setIsAnnual] = useState(false);
    const [isProcessing, setIsProcessing] = useState(null);

    const handleSelectPlan = async (plan) => {
        if (plan.id === "starter") {
            // Free plan - just close and go to main
            toast.success("Welcome to Site Grade Pro!");
            if (onBack) onBack();
            return;
        }

        if (plan.id === "agency") {
            // Contact sales - open consultation
            if (onSelectPlan) {
                onSelectPlan("consultation");
            }
            return;
        }

        // Pro plan - Stripe checkout
        setIsProcessing(plan.id);
        try {
            const planId = isAnnual ? `${plan.id}_annual` : `${plan.id}_monthly`;
            const response = await axios.post(`${API}/checkout`, {
                plan_id: planId,
                origin_url: window.location.origin,
                email: null
            });
            window.location.href = response.data.url;
        } catch (error) {
            console.error("Checkout failed:", error);
            toast.error("Failed to start checkout. Please try again.");
            setIsProcessing(null);
        }
    };

    const formatPrice = (price) => {
        if (price === 0) return "Free";
        return `$${price}`;
    };

    const getAnnualSavings = (monthly, annual) => {
        if (monthly === 0) return null;
        const savings = (monthly * 12) - annual;
        return savings;
    };

    const seo = SEO_CONFIG.pricing;
    
    return (
        <div className="min-h-screen bg-[#0a0a0a]">
            <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />
            
            {/* Subtle grid background */}
            <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
            
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-sm border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-8">
                        <button 
                            onClick={() => onNavigate ? onNavigate('home') : (onBack && onBack())}
                            className="flex items-center"
                        >
                            <img 
                                src={LOGO_URL} 
                                alt="SiteGradePro" 
                                className="h-10 w-auto object-contain"
                            />
                        </button>
                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex items-center gap-6">
                            <button onClick={() => onNavigate && onNavigate('features')} className="text-sm text-gray-400 hover:text-white transition-colors">Features</button>
                            <button onClick={() => onNavigate && onNavigate('how-it-works')} className="text-sm text-gray-400 hover:text-white transition-colors">How It Works</button>
                            <span className="text-sm text-magenta font-medium">Pricing</span>
                            <button onClick={() => onNavigate && onNavigate('about')} className="text-sm text-gray-400 hover:text-white transition-colors">About</button>
                            <button onClick={() => onNavigate && onNavigate('contact')} className="text-sm text-gray-400 hover:text-white transition-colors">Contact</button>
                            <button onClick={() => onNavigate && onNavigate('blog')} className="text-sm text-gray-400 hover:text-white transition-colors">Blog</button>
                        </nav>
                    </div>
                    <Button 
                        onClick={() => onNavigate ? onNavigate('home') : (onBack && onBack())}
                        className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-6"
                    >
                        Free Audit
                    </Button>
                </div>
            </header>

            {/* Main Content */}
            <main className="relative pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    
                    {/* Header Section */}
                    <div className="text-center mb-12 animate-fade-in">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-full text-violet-400 text-sm mb-6">
                            <Sparkles className="h-4 w-4" />
                            Simple, Transparent Pricing
                        </div>
                        
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
                            Choose Your Plan
                        </h1>
                        
                        <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
                            Start free and upgrade as you grow. All plans include our core website analysis tools.
                        </p>

                        {/* Billing Toggle */}
                        <div className="flex items-center justify-center gap-4">
                            <span className={`text-sm ${!isAnnual ? 'text-white' : 'text-gray-500'}`}>
                                Monthly
                            </span>
                            <button
                                onClick={() => setIsAnnual(!isAnnual)}
                                className={`relative w-14 h-7 rounded-full transition-colors ${
                                    isAnnual ? 'bg-violet-500' : 'bg-white/20'
                                }`}
                                data-testid="billing-toggle"
                            >
                                <div 
                                    className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                                        isAnnual ? 'translate-x-8' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                            <span className={`text-sm ${isAnnual ? 'text-white' : 'text-gray-500'}`}>
                                Annual
                            </span>
                            {isAnnual && (
                                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                                    Save 50%
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Pricing Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                        {PLANS.map((plan) => {
                            const Icon = plan.icon;
                            const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
                            const savings = isAnnual ? getAnnualSavings(plan.monthlyPrice, plan.annualPrice) : null;
                            const isPopular = plan.popular;
                            
                            return (
                                <Card 
                                    key={plan.id}
                                    className={`relative bg-[#111111] overflow-hidden transition-all hover:scale-[1.02] ${
                                        isPopular 
                                            ? 'border-violet-500 shadow-lg shadow-violet-500/20' 
                                            : 'border-white/10 hover:border-white/20'
                                    }`}
                                    data-testid={`plan-${plan.id}`}
                                >
                                    {/* Popular Badge */}
                                    {isPopular && (
                                        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-violet-500 to-purple-600 text-white text-center py-2 text-sm font-medium">
                                            <Star className="inline h-4 w-4 mr-1" />
                                            Most Popular
                                        </div>
                                    )}
                                    
                                    <CardContent className={`p-6 lg:p-8 ${isPopular ? 'pt-14' : ''}`}>
                                        {/* Plan Icon & Name */}
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                                plan.color === 'gray' ? 'bg-gray-500/10 border border-gray-500/20' :
                                                plan.color === 'violet' ? 'bg-violet-500/10 border border-violet-500/20' :
                                                'bg-emerald-500/10 border border-emerald-500/20'
                                            }`}>
                                                <Icon className={`h-6 w-6 ${
                                                    plan.color === 'gray' ? 'text-gray-400' :
                                                    plan.color === 'violet' ? 'text-violet-400' :
                                                    'text-emerald-400'
                                                }`} />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                                                <p className="text-sm text-gray-500">{plan.description}</p>
                                            </div>
                                        </div>

                                        {/* Price */}
                                        <div className="mb-6">
                                            <div className="flex items-baseline gap-1">
                                                <span className="text-4xl font-bold text-white">
                                                    {formatPrice(price)}
                                                </span>
                                                {price > 0 && (
                                                    <span className="text-gray-500">
                                                        /{isAnnual ? 'year' : 'month'}
                                                    </span>
                                                )}
                                            </div>
                                            {savings && isAnnual && (
                                                <p className="text-sm text-emerald-400 mt-1">
                                                    Save ${savings}/year
                                                </p>
                                            )}
                                            {!isAnnual && plan.monthlyPrice > 0 && (
                                                <p className="text-sm text-gray-500 mt-1">
                                                    Billed monthly
                                                </p>
                                            )}
                                        </div>

                                        {/* CTA Button */}
                                        <Button
                                            onClick={() => handleSelectPlan(plan)}
                                            disabled={isProcessing === plan.id}
                                            className={`w-full mb-6 py-5 font-semibold ${
                                                isPopular 
                                                    ? 'bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white shadow-lg shadow-violet-500/20' 
                                                    : plan.ctaVariant === 'outline'
                                                        ? 'bg-transparent border border-white/20 text-white hover:bg-white/5'
                                                        : 'bg-white text-black hover:bg-gray-200'
                                            }`}
                                            data-testid={`select-${plan.id}-btn`}
                                        >
                                            {isProcessing === plan.id ? 'Processing...' : plan.cta}
                                        </Button>

                                        {/* Features List */}
                                        <div className="space-y-3">
                                            {plan.features.map((feature, idx) => (
                                                <div 
                                                    key={idx}
                                                    className="flex items-center gap-3"
                                                >
                                                    {feature.included ? (
                                                        <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                                                            <Check className="h-3 w-3 text-emerald-400" />
                                                        </div>
                                                    ) : (
                                                        <div className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                                                            <X className="h-3 w-3 text-gray-600" />
                                                        </div>
                                                    )}
                                                    <span className={`text-sm ${
                                                        feature.included ? 'text-gray-300' : 'text-gray-600'
                                                    }`}>
                                                        {feature.text}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>

                    {/* Features Comparison */}
                    <div className="mt-20 text-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
                        <h2 className="text-2xl font-bold text-white mb-4">
                            All Plans Include
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-2 gap-6 max-w-2xl mx-auto mt-8">
                            <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                                <BarChart3 className="h-8 w-8 text-violet-400 mx-auto mb-3" />
                                <p className="text-white font-medium">AI-Powered Analysis</p>
                                <p className="text-sm text-gray-500 mt-1">Smart scoring system</p>
                            </div>
                            <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                                <Globe className="h-8 w-8 text-blue-400 mx-auto mb-3" />
                                <p className="text-white font-medium">Website Scoring</p>
                                <p className="text-sm text-gray-500 mt-1">Performance insights</p>
                            </div>
                        </div>

                        {/* Pro & Agency Only Features */}
                        <h3 className="text-xl font-bold text-white mt-12 mb-4">
                            Pro & Agency Only
                        </h3>
                        <p className="text-gray-500 text-sm mb-6">Upgrade to unlock these powerful features</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                            <div className="p-4 bg-violet-500/5 rounded-lg border border-violet-500/20">
                                <Calculator className="h-8 w-8 text-cyan-400 mx-auto mb-3" />
                                <p className="text-white font-medium">Revenue Tools</p>
                                <p className="text-sm text-gray-500 mt-1">Calculators & builders</p>
                            </div>
                            <div className="p-4 bg-violet-500/5 rounded-lg border border-violet-500/20">
                                <ShoppingBag className="h-8 w-8 text-green-400 mx-auto mb-3" />
                                <p className="text-white font-medium">Shopify Audit</p>
                                <p className="text-sm text-gray-500 mt-1">Store optimization</p>
                            </div>
                            <div className="p-4 bg-violet-500/5 rounded-lg border border-violet-500/20">
                                <FileText className="h-8 w-8 text-orange-400 mx-auto mb-3" />
                                <p className="text-white font-medium">Detailed Reports</p>
                                <p className="text-sm text-gray-500 mt-1">Actionable insights</p>
                            </div>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <div className="mt-20 max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.3s' }}>
                        <h2 className="text-2xl font-bold text-white text-center mb-8">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-4">
                            <div className="p-5 bg-white/5 rounded-lg border border-white/10">
                                <h3 className="text-white font-medium mb-2">Can I cancel anytime?</h3>
                                <p className="text-gray-400 text-sm">Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period.</p>
                            </div>
                            <div className="p-5 bg-white/5 rounded-lg border border-white/10">
                                <h3 className="text-white font-medium mb-2">What payment methods do you accept?</h3>
                                <p className="text-gray-400 text-sm">We accept all major credit cards through Stripe, our secure payment processor.</p>
                            </div>
                            <div className="p-5 bg-white/5 rounded-lg border border-white/10">
                                <h3 className="text-white font-medium mb-2">Is there a free trial?</h3>
                                <p className="text-gray-400 text-sm">The Starter plan is completely free forever. Pro plans include a 7-day money-back guarantee.</p>
                            </div>
                            <div className="p-5 bg-white/5 rounded-lg border border-white/10">
                                <h3 className="text-white font-medium mb-2">What's included in white label reports?</h3>
                                <p className="text-gray-400 text-sm">Agency plan reports can be fully customized with your own branding, logo, and color scheme. Perfect for client deliverables.</p>
                            </div>
                        </div>
                    </div>

                    {/* Bottom CTA */}
                    <div className="mt-20 text-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
                        <p className="text-gray-400 mb-4">
                            Have questions? We're here to help.
                        </p>
                        <Button
                            onClick={() => onSelectPlan && onSelectPlan("consultation")}
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/5"
                            data-testid="contact-sales-btn"
                        >
                            Contact Sales
                        </Button>
                    </div>
                </div>
            </main>
        </div>
    );
}
